// 2026-05-26 - FormuLearning - Step 4 §4.9 최적화 완료
// 2026-09-01 수정 - 이슈 #58 S10 3차(#70, M84-36·69): `COMP-OPTIMIZATION-COMPLETE`.
//                   **최종 처방(배치)을 고른 뒤** 완료한다 + **최종 처방표 다운로드**.
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 최종 처방 선택·완료 비활성(최종 처방 다운로드는 그대로).
// 파일 위치: frontend/src/features/step4/CompletionSection.tsx
//
// 화면 규약 넷.
//   ① 최종 처방을 **고르기 전에는 완료 버튼이 비활성**이다. 요건이 *"최종 처방을 고른 뒤
//      완료"* 이므로 선택 없이 완료되는 경로가 남으면 그 자리에서 무력화된다. 같은 이유로
//      3차에서 `Stop Optimization`(다른 완료 진입점)을 걷었다.
//   ② 후보에서 **제외 배치는 뺀다**. 계산에서 뺀 배치를 결론으로 삼는 것은 자기모순이며
//      서버도 422 로 되받는다.
//   ③ 완료 뒤에는 선택기를 내리고 **무엇으로 끝냈는지 표시로 남긴다.** 되돌릴 수 없는
//      결정이라 «고를 것» 이 아니라 «기록» 이 된다.
//   ④ 최종 처방표는 **완료 전후 모두** 받을 수 있다. 전 라운드 정리본이라 완료를
//      판단하려면 오히려 완료 전에 필요하다.

import { useState } from 'react'
import { downloadFile, finalPrescriptionUrl } from '../../api/step4'
import { apiErrorDetail, type LotBatch } from '../../api/lot'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  projectId: string
  /** 최종 처방 후보 — 프로젝트 전 배치. 제외분은 이 컴포넌트가 뺀다. */
  batches: LotBatch[]
  isCompleted: boolean
  completedAt: string | null
  /** 완료 시 확정된 최종 처방(M84-36). 완료 전에는 `null`. */
  finalBatchId: string | null
  onComplete: (finalBatchId: string) => Promise<void>
  completing: boolean
}

export default function CompletionSection({
  projectId, batches, isCompleted, completedAt, finalBatchId, onComplete, completing,
}: Props) {
  const canWrite = useCanWrite()
  const roTitle = canWrite ? undefined : READ_ONLY_TITLE
  const candidates = batches.filter((b) => !b.is_excluded)
  const [selectedId, setSelectedId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const finalBatch = batches.find((b) => b.id === finalBatchId) ?? null

  const handleDownload = async () => {
    await downloadFile(finalPrescriptionUrl(projectId), 'step4_final_prescription.xlsx')
  }

  const handleComplete = async () => {
    if (!selectedId) return
    setError(null)
    try {
      await onComplete(selectedId)
    } catch (e) {
      // 422(제외 배치·대조약·타 프로젝트)는 서버 사유를 그대로 보인다.
      setError(apiErrorDetail(e) ?? '완료 처리에 실패했습니다.')
    }
  }

  const downloadButton = (
    <button
      type="button"
      onClick={() => void handleDownload()}
      data-testid="final-prescription-download"
      className="rounded border px-3 py-1.5 text-[11px] font-semibold text-text-body transition-opacity hover:opacity-90"
      style={{ borderColor: 'var(--border-purple)', backgroundColor: 'var(--bg-chip-purple)' }}
      title="전 라운드의 처방·결과를 한 파일로 정리해 내려받습니다"
    >
      ⬇ 최종 처방표 (.xlsx)
    </button>
  )

  return (
    <div className="mb-4" data-testid="completion-section">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
        Optimization Completion
      </h4>
      <div className="rounded border p-3" style={{ borderColor: 'var(--border-subtle)' }}>
        {isCompleted ? (
          <div>
            <p className="text-[11px] font-semibold text-accent-teal">Optimization completed</p>
            <p className="mt-1 text-[11px] text-text-body" data-testid="final-batch-label">
              최종 처방: <b style={{ color: 'var(--color-accent-teal)' }}>
                {finalBatch?.batch_code ?? '미지정'}
              </b>
              {finalBatch?.f2 != null && (
                <span className="ml-1.5 text-text-muted">· f2 {finalBatch.f2.toFixed(1)}</span>
              )}
            </p>
            {completedAt && (
              <p className="mt-1 text-[10px] text-text-muted">
                Completed at: {new Date(completedAt).toLocaleString()}
              </p>
            )}
            <div className="mt-2">{downloadButton}</div>
          </div>
        ) : (
          <div>
            <p className="mb-2 text-[11px] text-text-body">
              최적화를 끝내려면 <b>최종 처방을 먼저 고르십시오.</b> 완료하면 진행 단계가
              Step 5 로 넘어가고 보고서 초안이 만들어집니다.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="final-batch" className="text-[11px] text-text-secondary">최종 처방 선택</label>
              <select
                id="final-batch"
                value={selectedId}
                disabled={completing || candidates.length === 0 || !canWrite}
                title={roTitle}
                onChange={(e) => { setSelectedId(e.target.value); setError(null) }}
                data-testid="final-batch-select"
                className="rounded bg-bg-input px-1.5 py-1 text-[11px] outline-none disabled:opacity-50"
                style={{ color: 'var(--color-accent-teal)', border: '1px solid var(--border-soft)' }}
              >
                <option value="">— 선택하십시오 —</option>
                {candidates.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.batch_code}{b.f2 != null ? ` · f2 ${b.f2.toFixed(1)}` : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => void handleComplete()}
                disabled={completing || !selectedId || !canWrite}
                title={roTitle}
                data-testid="complete-optimization"
                className="rounded px-4 py-1.5 text-[11px] font-semibold text-accent-teal transition-colors hover:bg-bg-input disabled:opacity-40"
                style={{ border: '1px solid var(--border-teal)' }}
              >
                {completing ? 'Completing...' : '✓ Complete Optimization'}
              </button>
              {downloadButton}
            </div>
            {candidates.length === 0 && (
              <p className="mt-2 text-[10px] text-warn">
                고를 수 있는 처방이 없습니다 — 배치를 만들거나 제외를 풀어 주십시오.
              </p>
            )}
            {error && (
              <p className="mt-2 text-[10px] text-warn" data-testid="complete-error">{error}</p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
